import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormControl} from "@angular/forms";
import {combineLatest, debounceTime, map, Observable, of, startWith, switchMap} from "rxjs";
import {CfProject, CfTask} from "@app-web-central/web/shared/data-access/models";
import {NzDrawerRef, NzDrawerService} from "ng-zorro-antd/drawer";
import {TaskStore} from "@app-web-central/web/task/data-access";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {RouteUtil, TaskUtil} from "@app-web-central/web/shared/utils";
import {Router} from "@angular/router";
import {CacheStore} from "@app-web-central/web/shared/data-access/store";
import {ProjectStore} from "@app-web-central/web/project/data-access";
import {TaskDrawerComponent} from "@app-web-central/web/task/ui/task-drawer";

@UntilDestroy()
@Component({
  selector: 'as-drawer-search',
  templateUrl: './drawer-search.component.html',
  styleUrls: ['./drawer-search.component.scss'],
  providers: [TaskStore]
})
export class DrawerSearchComponent implements OnInit {
  @ViewChild("TaskTitleTmpl") TaskTitleTmpl!: TemplateRef<any>;
  searchControl: FormControl = new FormControl('');
  results$!: Observable<CfTask[]>;
  recentTasks$ = new Observable<CfTask[]>();
  projects!: CfProject[];

  get hasSearchTermInput(): boolean {
    return !!this.searchControl.value;
  }

  constructor(
    private _router: Router,
    private _store: TaskStore,
    private _cache: CacheStore,
    private _drawer: NzDrawerRef,
    public projectStore: ProjectStore,
    private _drawerService: NzDrawerService,
  ) { }

  ngOnInit(): void {
    const search$ = this.searchControl.valueChanges.pipe(debounceTime(50), startWith(this.searchControl.value));
    this.recentTasks$ = this._cache.searchCache$.pipe(map((tasks) => tasks !== null ? tasks.slice(0, 15) : []));
    this.results$ = combineLatest([search$, this._store.tasks$]).pipe(
      untilDestroyed(this),
      switchMap(([term, tasks]) => {
        const matchTasks = tasks?.filter((task) => {
          const foundInNumber = TaskUtil.searchString(task.identifier, term);
          const foundInName = TaskUtil.searchString(task.name, term);
          const foundInDescription = TaskUtil.searchString(task.description, term);
          const foundInTags = TaskUtil.searchStringInArray(task.tags, term);
          return foundInNumber || foundInName || foundInDescription || foundInTags;
        });
        return of(matchTasks || []);
      })
    );
    this.projectStore.projects$
      .pipe(untilDestroyed(this))
      .subscribe((projects) => {
        if (projects) {
          this.projects = projects;
        }
      });
  }

  closeDrawer() {
    this._drawer.close();
  }

  goToTask(task: CfTask) {
    this._cache.addToLocaleCache$(task);
    if (this.projects) {
      const projectKey = this.projects.find((p) => p.id === task.projectId)?.key
      const routeUrl = RouteUtil.getEventsRouteUrl(projectKey as string);
      this._router.navigateByUrl(`${routeUrl}/${task.id}`);
    }
    this.closeDrawer();
  }

  openTaskDrawer(task: CfTask, project: CfProject) {
    this._cache.addToLocaleCache$(task);
    this._router.navigate([], {
        queryParams: {taskId: task.id}, queryParamsHandling: 'merge'
      }
    );
    const drawer = this._drawerService.create({
      nzContent: TaskDrawerComponent,
      nzContentParams: { project },
      nzTitle: this.TaskTitleTmpl,
      nzWidth: '100%',
      nzClosable: true,
      nzWrapClassName: 'nz-task-drawer',
      nzBodyStyle: {padding: 0}
    });
    drawer.afterClose
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this._router.navigate([], {
          queryParams: {taskId: null},
          queryParamsHandling: 'merge'
        });
      });
  }

}
